import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Brain, Loader2, RefreshCw, ChevronLeft, ChevronRight, BookMarked, Check } from "lucide-react";
import { toast, Toaster } from "sonner";

const PRESETS = ["Cépages rouges", "Cépages blancs", "Bourgogne", "Bordeaux", "Spiritueux", "Italie"];

export default function Flashcards() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("Cépages rouges");
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [savingSRS, setSavingSRS] = useState(false);
  const [srsSaved, setSrsSaved] = useState(false);

  const generate = async (t) => {
    setBusy(true);
    setSrsSaved(false);
    try {
      const { data } = await api.post("/flashcards/generate", { topic: t, count: 10 });
      setCards(data.cards || []);
      setIdx(0); setFlipped(false);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const saveDeckToSRS = async () => {
    if (!user) { toast.error("Connectez-vous pour ajouter à vos révisions."); return; }
    setSavingSRS(true);
    try {
      const { data } = await api.post("/flashcards/save-to-srs", { topic, cards });
      setSrsSaved(true);
      toast.success(`📚 ${data.added} carte(s) ajoutée(s) à vos révisions SM-2`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSavingSRS(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <span className="chip-pink">Révision</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Flashcards</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">Une thématique, 10 cartes, vous retournez et révisez à votre rythme.</p>

      {cards.length === 0 && (
        <div className="mt-8">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p} onClick={() => { setTopic(p); generate(p); }} disabled={busy}
                className="px-3.5 py-1.5 rounded-full border border-[#1F3A5F]/12 bg-white hover:bg-[#FBF7F3] text-sm text-[#1F3A5F]">{p}</button>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3 max-w-md">
            <input value={topic} onChange={(e) => setTopic(e.target.value)} className="flex-1 bg-white border border-[#1F3A5F]/15 rounded-lg px-3 py-2 text-sm" placeholder="Sujet personnalisé…" />
            <button onClick={() => generate(topic)} disabled={busy} data-testid="flashcards-generate" className="btn-primary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}Générer
            </button>
          </div>
        </div>
      )}

      {busy && cards.length === 0 && (
        <div className="mt-12 text-center text-[#8B95A1]"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#E5276A]" /></div>
      )}

      {cards.length > 0 && (
        <div className="mt-10">
          <div className="text-center text-xs text-[#8B95A1] mb-3">Carte {idx + 1} / {cards.length}</div>
          <button onClick={() => setFlipped((v) => !v)} data-testid="flashcard-card"
            className="w-full app-card aspect-[3/2] flex items-center justify-center p-8 text-center cursor-pointer hover:border-[#E5276A]">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">{flipped ? "Réponse" : "Question"}</div>
              <div className="font-display text-2xl md:text-3xl text-[#1F3A5F] leading-snug">{flipped ? cards[idx].back : cards[idx].front}</div>
              {!flipped && <div className="mt-6 text-xs text-[#8B95A1] italic">Touchez pour retourner</div>}
            </div>
          </button>
          <div className="mt-5 flex items-center justify-between">
            <button onClick={() => { setIdx((i) => Math.max(0, i - 1)); setFlipped(false); }} disabled={idx === 0}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-[#1F3A5F]/12 bg-white disabled:opacity-40 text-sm text-[#1F3A5F]"><ChevronLeft className="w-4 h-4" /> Préc.</button>
            <button onClick={() => { setCards([]); setIdx(0); setFlipped(false); setSrsSaved(false); }} className="text-xs text-[#8B95A1] hover:text-[#E5276A]"><RefreshCw className="w-3 h-3 inline" /> Nouveau sujet</button>
            <button onClick={() => { setIdx((i) => Math.min(cards.length - 1, i + 1)); setFlipped(false); }} disabled={idx === cards.length - 1}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#E5276A] text-white disabled:opacity-40 text-sm font-bold">Suiv. <ChevronRight className="w-4 h-4" /></button>
          </div>

          {/* ═════════ SAVE TO SRS ═════════ */}
          <div className="mt-8 rounded-2xl border-2 border-dashed border-[#1F3A5F]/15 bg-[#FBF7F3] p-5 flex items-start gap-4 flex-wrap" data-testid="flashcards-srs-cta">
            <div className="w-12 h-12 rounded-xl bg-[#1F3A5F] flex items-center justify-center shrink-0">
              <BookMarked className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[#1F3A5F]">Sauvegarder ce deck dans vos révisions</p>
              <p className="text-xs text-[#4A5568] mt-1">
                Les cartes rejoindront votre file de révisions espacées (SM-2). Elles réapparaîtront automatiquement au bon moment pour ancrer les connaissances sur le long terme.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={saveDeckToSRS}
                  disabled={savingSRS || srsSaved || !user}
                  data-testid="flashcards-save-srs"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870] disabled:opacity-60"
                >
                  {savingSRS ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    srsSaved ? <Check className="w-4 h-4" /> : <BookMarked className="w-4 h-4" />}
                  {srsSaved ? "Ajouté à vos révisions" : "Ajouter à mes révisions"}
                </button>
                {srsSaved && (
                  <Link to="/microlearning" className="text-xs font-bold text-[#E5276A] hover:underline">
                    Voir mes révisions →
                  </Link>
                )}
                {!user && <span className="text-xs text-[#8B95A1]">Connectez-vous pour sauvegarder</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
